import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectPicker } from 'rsuite';
import * as requestsApi from '../../api/requests';
import * as coursesApi from '../../api/courses';
import * as statsApi from '../../api/stats';
import PageLayout from '../../common/PageLayout';
import { formatDateTime } from '../../common/formatters';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
// "Month" here is a rolling 30-day window from today, not a calendar month.
const MONTH_MS = 30 * DAY_MS;

const STATS_MIN_YEAR = 2024;
const CURRENT_YEAR = new Date().getFullYear();
// Year options for the breakdown panels: current year down to 2024.
const YEAR_OPTIONS = Array.from(
  { length: Math.max(1, CURRENT_YEAR - STATS_MIN_YEAR + 1) },
  (_, idx) => {
    const year = CURRENT_YEAR - idx;
    return { label: String(year), value: year };
  }
);

interface BreakdownItem {
  label: string;
  count: number;
}

const fetchStatusBreakdown = async (year: number): Promise<BreakdownItem[]> => {
  const res = await statsApi.statsByStatus(year);
  return res.statuses.map(s => ({ label: s.label, count: s.count }));
};

const fetchCourseBreakdown = async (year: number): Promise<BreakdownItem[]> => {
  const res = await statsApi.statsByCourse(year);
  return res.courses.map(c => ({ label: c.courseName, count: c.count }));
};

interface RequestItem {
  id: string;
  email: string;
  firstName: string;
  secondName: string;
  childFullName?: string;
  status?: number;
  statusName?: string;
  course?: string;
  courses?: { id: string; name: string }[];
  createdAt: number;
}

interface CourseItem {
  id: string;
  name: string;
  acceptsRequests: boolean;
  queueLength: number;
  requestsAmount: number;
}

interface DashboardData {
  loading: boolean;
  totalRequests: number;
  requestsThisWeek: number;
  requestsPrevWeek: number;
  requestsThisMonth: number;
  requestsPrevMonth: number;
  pendingRequests: number;
  activeCourses: number;
  totalCourses: number;
  totalQueueLength: number;
  recentRequests: RequestItem[];
}

const initialData: DashboardData = {
  loading: true,
  totalRequests: 0,
  requestsThisWeek: 0,
  requestsPrevWeek: 0,
  requestsThisMonth: 0,
  requestsPrevMonth: 0,
  pendingRequests: 0,
  activeCourses: 0,
  totalCourses: 0,
  totalQueueLength: 0,
  recentRequests: [],
};

function formatDelta(current: number, previous: number) {
  if (previous === 0) {
    return current > 0 ? `+${current}` : '—';
  }
  const diff = current - previous;
  const sign = diff > 0 ? '+' : '';
  const pct = Math.round((diff / previous) * 100);
  return `${sign}${pct}%`;
}

function getDeltaDirection(
  current: number,
  previous: number
): 'up' | 'down' | null {
  if (current > previous) {
    return 'up';
  }
  if (current < previous) {
    return 'down';
  }
  return null;
}

function getInitials(req: RequestItem): string {
  const a = (req.firstName || '').trim();
  const b = (req.secondName || '').trim();
  if (a || b) {
    return `${a[0] ?? ''}${b[0] ?? ''}`.toUpperCase() || '?';
  }
  return (req.email[0] || '?').toUpperCase();
}

// Course label for a request: the structured M2M courses, falling back to
// the free-text `course` field, then to a placeholder.
function getCourseLabel(req: RequestItem): string {
  const names = (req.courses || [])
    .map(c => c?.name)
    .filter((name): name is string => Boolean(name));
  if (names.length > 0) {
    return names.join(', ');
  }
  return req.course?.trim() || 'Без курсу';
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = React.useState<DashboardData>(initialData);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const now = Date.now();
        const weekStart = now - WEEK_MS;
        const prevWeekStart = now - 2 * WEEK_MS;
        const monthStart = now - MONTH_MS;
        const prevMonthStart = now - 2 * MONTH_MS;

        const [recentRes, windowRes, coursesRes] = await Promise.all([
          requestsApi.list(
            1,
            5,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            'createdAt',
            'DESC'
          ),
          // A 60-day window covers both the rolling month and its previous
          // month, plus the week and pending counts.
          requestsApi.list(
            1,
            200,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            'createdAt',
            'DESC',
            [prevMonthStart, now]
          ),
          coursesApi.list('createdAt', 'DESC'),
        ]);

        if (cancelled) return;

        const recent: RequestItem[] = recentRes?.data || [];
        const recentTotal: number = recentRes?.totalAmount || 0;

        const windowRequests: RequestItem[] = windowRes?.data || [];
        const countInRange = (from: number, to: number) =>
          windowRequests.filter(r => r.createdAt >= from && r.createdAt < to)
            .length;

        const thisWeek = windowRequests.filter(
          r => r.createdAt >= weekStart
        ).length;
        const prevWeek = countInRange(prevWeekStart, weekStart);
        const thisMonth = windowRequests.filter(
          r => r.createdAt >= monthStart
        ).length;
        const prevMonth = countInRange(prevMonthStart, monthStart);

        const courses: CourseItem[] = coursesRes?.courses || [];
        // "Active" here means a course with registration currently open.
        const activeCourses = courses.filter(c => c.acceptsRequests).length;
        const totalQueueLength = courses.reduce(
          (sum, c) => sum + (c.queueLength || 0),
          0
        );

        // Pending requests over the last 30 days (matches the card footer).
        const pendingRequests = windowRequests.filter(
          r => r.status === 0 && r.createdAt >= monthStart
        ).length;

        setData({
          loading: false,
          totalRequests: recentTotal,
          requestsThisWeek: thisWeek,
          requestsPrevWeek: prevWeek,
          requestsThisMonth: thisMonth,
          requestsPrevMonth: prevMonth,
          pendingRequests,
          activeCourses,
          totalCourses: courses.length,
          totalQueueLength,
          recentRequests: recent,
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setData(prev => ({ ...prev, loading: false }));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const subtitle = data.loading
    ? 'Завантаження…'
    : `Останнє оновлення: ${new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <PageLayout title="Головна" subtitle={subtitle}>
      <div className="dashboard">
        <div className="dashboard-stats">
          <StatCard
            label="Анкет за тиждень"
            value={data.requestsThisWeek}
            delta={formatDelta(data.requestsThisWeek, data.requestsPrevWeek)}
            deltaDirection={getDeltaDirection(
              data.requestsThisWeek,
              data.requestsPrevWeek
            )}
            footer={`Всього в системі: ${data.totalRequests}`}
            icon={<IconRequests />}
            actionLabel="Анкети →"
            onAction={() => navigate('/requests')}
          />
          <StatCard
            label="Анкет за місяць"
            value={data.requestsThisMonth}
            delta={formatDelta(data.requestsThisMonth, data.requestsPrevMonth)}
            deltaDirection={getDeltaDirection(
              data.requestsThisMonth,
              data.requestsPrevMonth
            )}
            footer="Останні 30 днів"
            icon={<IconCalendar />}
            actionLabel="Анкети →"
            onAction={() => navigate('/requests')}
          />
          <StatCard
            label="Анкет на розгляді"
            value={data.pendingRequests}
            footer="Останні 30 днів"
            icon={<IconClock />}
            tone="accent"
            actionLabel="Анкети →"
            onAction={() => navigate('/requests')}
          />
          <StatCard
            label="Активних курсів"
            value={data.activeCourses}
            footer={`З ${data.totalCourses} загалом • ${data.totalQueueLength} у чергах`}
            icon={<IconBook />}
            tone="success"
            actionLabel="Курси →"
            onAction={() => navigate('/courses')}
          />
        </div>

        <div className="dashboard-row dashboard-row--split">
          <section className="dashboard-panel">
            <header className="dashboard-panel__header">
              <h2 className="dashboard-panel__title">Останні анкети</h2>
              <button
                className="rs-btn rs-btn-link"
                onClick={() => navigate('/requests')}
                type="button"
              >
                Усі →
              </button>
            </header>
            {data.loading ? (
              <ActivitySkeleton />
            ) : data.recentRequests.length === 0 ? (
              <p className="dashboard-panel__hint" style={{ margin: 0 }}>
                Ще немає жодної анкети.
              </p>
            ) : (
              <ul className="activity-list">
                {data.recentRequests.map(req => (
                  <li
                    key={req.id}
                    className="activity-item"
                    onClick={() => navigate('/requests')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') navigate('/requests');
                    }}
                  >
                    <div className="activity-item__avatar">
                      {getInitials(req)}
                    </div>
                    <div className="activity-item__body">
                      <p className="activity-item__title">
                        {req.childFullName ||
                          `${req.firstName} ${req.secondName}`.trim() ||
                          req.email}
                      </p>
                      <span className="activity-item__course">
                        {getCourseLabel(req)}
                      </span>
                      <span className="activity-item__meta">
                        {req.statusName || '—'} ·{' '}
                        {formatDateTime(req.createdAt)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="dashboard-stack">
            <BreakdownPanel
              title="Унікальні анкети за статусами"
              emptyText="Немає анкет за обраний рік."
              fetcher={fetchStatusBreakdown}
            />
            <BreakdownPanel
              title="Унікальні анкети за курсами"
              emptyText="Немає анкет із курсами за обраний рік."
              fetcher={fetchCourseBreakdown}
              scroll
              wideLabel
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

function BreakdownPanel({
  title,
  emptyText,
  fetcher,
  scroll,
  wideLabel,
}: {
  title: string;
  emptyText: string;
  fetcher: (year: number) => Promise<BreakdownItem[]>;
  scroll?: boolean;
  wideLabel?: boolean;
}) {
  const [year, setYear] = React.useState<number>(CURRENT_YEAR);
  const [items, setItems] = React.useState<BreakdownItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcher(year)
      .then(rows => {
        if (!cancelled) {
          setItems(rows);
          setLoading(false);
        }
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) {
          setItems([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [year, fetcher]);

  return (
    <section className="dashboard-panel">
      <header className="dashboard-panel__header">
        <h2 className="dashboard-panel__title">{title}</h2>
        <SelectPicker
          className="dashboard-panel__year"
          data={YEAR_OPTIONS}
          value={year}
          onChange={value => {
            if (value != null) {
              setYear(value);
            }
          }}
          cleanable={false}
          searchable={false}
          size="sm"
        />
      </header>
      {loading ? (
        <BarListSkeleton />
      ) : items.length === 0 ? (
        <p className="dashboard-panel__hint" style={{ margin: 0 }}>
          {emptyText}
        </p>
      ) : (
        <BarList items={items} scroll={scroll} wideLabel={wideLabel} />
      )}
    </section>
  );
}

function BarList({
  items,
  scroll,
  wideLabel,
}: {
  items: BreakdownItem[];
  scroll?: boolean;
  wideLabel?: boolean;
}) {
  const max = Math.max(1, ...items.map(i => i.count));
  const className = [
    'bar-list',
    scroll ? 'bar-list--scroll' : '',
    wideLabel ? 'bar-list--wide-label' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <ul className={className}>
      {items.map((item, idx) => (
        <li key={`${item.label}-${idx}`} className="bar-list__row">
          <span className="bar-list__label" title={item.label}>
            {item.label}
          </span>
          <span className="bar-list__track">
            <span
              className="bar-list__fill"
              style={{ width: `${Math.round((item.count / max) * 100)}%` }}
            />
          </span>
          <span className="bar-list__value">{item.count}</span>
        </li>
      ))}
    </ul>
  );
}

function BarListSkeleton() {
  return (
    <ul className="bar-list" aria-hidden="true">
      {[0, 1, 2, 3, 4].map(i => (
        <li key={i} className="bar-list__row">
          <span className="skeleton-cell" style={{ width: 90 }} />
          <span className="skeleton-cell" style={{ flex: 1 }} />
          <span className="skeleton-cell" style={{ width: 24 }} />
        </li>
      ))}
    </ul>
  );
}

function StatCard({
  label,
  value,
  delta,
  deltaDirection,
  footer,
  icon,
  tone,
  actionLabel,
  onAction,
}: {
  label: string;
  value: number;
  delta?: string;
  deltaDirection?: 'up' | 'down' | null;
  footer?: string;
  icon: React.ReactNode;
  tone?: 'accent' | 'success' | 'danger';
  actionLabel?: string;
  onAction?: () => void;
}) {
  const iconClass = tone
    ? `stat-card__icon stat-card__icon--${tone}`
    : 'stat-card__icon';
  return (
    <div className="stat-card">
      <span className="stat-card__label">{label}</span>
      <div className="stat-card__value-row">
        <span className="stat-card__value">{value}</span>
        {delta && deltaDirection ? (
          <span
            className={`stat-card__delta stat-card__delta--${deltaDirection}`}
          >
            {deltaDirection === 'up' ? '▲' : '▼'} {delta}
          </span>
        ) : null}
      </div>
      {footer ? <div className="stat-card__footer">{footer}</div> : null}
      {actionLabel && onAction ? (
        <button type="button" className="stat-card__action" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
      <div className={iconClass}>{icon}</div>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <ul className="activity-list" aria-hidden="true">
      {[0, 1, 2, 3].map(i => (
        <li key={i} className="activity-item">
          <div
            className="skeleton-cell"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <span className="skeleton-cell" style={{ width: '60%' }} />
            <span className="skeleton-cell" style={{ width: '45%' }} />
            <span className="skeleton-cell" style={{ width: '35%' }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

const IconRequests = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <path d="M7 9h10M7 13h10M7 17h6" />
  </svg>
);
const IconCalendar = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="17" rx="3" />
    <path d="M3 10h18M8 2v4M16 2v4" />
  </svg>
);
const IconClock = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const IconBook = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2V5z" />
    <path d="M4 19c0-1.1.9-2 2-2h12" />
  </svg>
);
